import { Email } from "@mui/icons-material";
import { Box, Button, FormHelperText, IconButton, TextField } from "@mui/material";
import { FC } from "react";
import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import * as Yup from "yup";
import useMounted from "hooks/useMounted";
import useLoginGuard from "hooks/useLoginGuard";
import toast from "react-hot-toast";
import useAuth from "hooks/useAuth";

const SendEmailLinkFormFormik: FC = props => {
    const mounted = useMounted();
    const { settings, saveSettings } = useLoginGuard();
    const { passwordRecovery } = useAuth();

    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Formik
                initialValues={{
                    email: "",
                    submit: null
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string()
                        .email("Must be a valid email")
                        .max(255)
                        .required("Email is required")
                })}
                onSubmit={async (
                    values,
                    { setErrors, setStatus, setSubmitting }
                ): Promise<void> => {
                    try {
                        await passwordRecovery(values.email);
                        saveSettings({
                            ...settings,
                            currStep: "RESET_PASSWORD",
                            currEmail: values.email
                        });
                        if (mounted.current) {
                            setStatus({ success: true });
                            setSubmitting(false);
                        }
                    } catch (error: any) {
                        if (mounted.current) {
                            setStatus({ success: false });
                            setErrors({ submit: error.errorMessage || error.message });
                            setSubmitting(false);
                        }
                        const { data, status } = error;
                        toast.error(
                            `Error code: ${status} - ${data?.message || data?.errorMessage}`
                        );
                        console.log("send email ===>", error);
                    }
                }}>
                {({
                    errors,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values
                }): JSX.Element => (
                    <form noValidate onSubmit={handleSubmit} {...props} style={{ width: "100%" }}>
                        <input
                            type="text"
                            name="_prevent_auto_complete_name"
                            autoComplete="off"
                            readOnly
                            style={{ display: "none" }}
                        />
                        <input
                            type="password"
                            name="_prevent_auto_complete_pass"
                            autoComplete="new-password"
                            readOnly
                            style={{ display: "none" }}
                        />
                        <TextField
                            autoFocus
                            error={Boolean(touched.email && errors.email)}
                            fullWidth
                            helperText={touched.email && errors.email}
                            label="Email"
                            name="email"
                            onChange={handleChange}
                            type="email"
                            value={values.email}
                            variant="standard"
                            InputProps={{
                                endAdornment: (
                                    <IconButton disableRipple disableTouchRipple>
                                        <Email fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                        {errors.submit && (
                            <Box>
                                <FormHelperText error>{errors.submit}</FormHelperText>
                            </Box>
                        )}
                        <Box
                            sx={{
                                mt: 5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                            <Button
                                color="primary"
                                onClick={() => {
                                    saveSettings({
                                        ...settings,
                                        currStep: "LOGIN"
                                    });
                                }}>
                                CANCEL
                            </Button>
                            <LoadingButton
                                color="primary"
                                sx={{ minWidth: 118, height: 39, fontSize: 14 }}
                                loading={isSubmitting}
                                size="large"
                                type="submit"
                                variant="contained">
                                SEND CODE
                            </LoadingButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default SendEmailLinkFormFormik;
